import { Box, FormHelperText, Grid, Typography } from "@mui/material"
import { memo } from "react"
import ErrorScreen from "../../../../../core-components/theme/components/ErrorScreen"
import { EscalationFormData, EscalationFormErrors, EscalationGroup } from "../../../types"
import { useEscalationMetaData } from "../hooks/useEscalationMetaData"
import AttendanceSection from "./AttendanceSection"
import EscalationGroupsSection from "./EscalationGroupsSection"
import ModuleSection from "./ModuleSection"
import PlantSection from "./PlantSection"
import UserGroupSection from "./UserGroupSection"

interface EscalationFormContentProps {
  formData: EscalationFormData
  errors: EscalationFormErrors
  onFormChange: <K extends keyof EscalationFormData>(field: K, value: EscalationFormData[K]) => void
  onAddEscalationGroup: () => void
  onRemoveEscalationGroup: (groupId: string) => void
  onEscalationGroupUpdate: (groupId: string, updates: Partial<EscalationGroup>) => void;
}

const EscalationFormContent = memo(
  ({
    formData,
    errors,
    onFormChange,
    onAddEscalationGroup,
    onRemoveEscalationGroup,
    onEscalationGroupUpdate
  }: EscalationFormContentProps) => {
    const {
      escalationUsers,
      escalationAssets,
      isEscalationMetaDataError,
    } = useEscalationMetaData(formData.userGroup)
    const isAttendanceSelected = formData.modules.includes('Attendance');
    const isTaskEscalationSelected = formData.modules.includes('Task Escalation');

    if(isEscalationMetaDataError){
      return (
        <ErrorScreen />
      )
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <UserGroupSection
              value={formData.userGroup}
              error={errors.userGroup}
              onChange={(value) => onFormChange("userGroup", value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PlantSection
              disabled={!formData.userGroup}
              value={formData.plants}
              defaultOptions={escalationAssets}
              error={errors.plants}
              onChange={(value) => onFormChange("plants", value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ModuleSection
              disabled={!formData.userGroup}
              value={formData.modules}
              error={errors.modules}
              onChange={(value) => onFormChange("modules", value)}
            />
          </Grid>
        </Grid>

        {isAttendanceSelected && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Attendance
            </Typography>
            <AttendanceSection
              options={escalationUsers}
              attendanceManager={formData.attendanceManager}
              rosterManager={formData.rosterManager}
              operator={formData.operator}
              errors={{
                attendanceManager: errors.attendanceManager,
                rosterManager: errors.rosterManager,
                operator: errors.operator,
              }}
              onChange={(field, value) => onFormChange(field, value)}
            />
          </Box>
        )}

        {isTaskEscalationSelected && (
          <Box>
            <Typography variant="subtitle2" gutterBottom >
              Task Escalation Groups
            </Typography>
            <EscalationGroupsSection
              options={escalationUsers}
              escalationGroups={formData.escalationGroups}
              errors={typeof errors.escalationGroups === 'object' && errors.escalationGroups !== null ? errors.escalationGroups : {}}
              onUpdateEscalationGroup={onEscalationGroupUpdate}
              onAddEscalationGroup={onAddEscalationGroup}
              onRemoveEscalationGroup={onRemoveEscalationGroup}
            />
            {typeof errors.escalationGroups === 'string' && (
                  <FormHelperText error sx={{ mt: 1 }}>
                      {errors.escalationGroups}
                  </FormHelperText>
              )}
          </Box>
        )}
      </Box>
    )
  },
)

export default EscalationFormContent;